import type { PaletteMode } from '@mui/material/styles'

/**
 * Pastels du design system Ledger : la gamme des tags que l'utilisateur pose sur ses dossiers.
 * Chaque teinte est un trio figé fond, bordure, texte ; le texte est assombri pour tenir 4,5:1
 * sur son fond. L'API ne stocke que le fond (`Tag.color`).
 */
export type TagPastel =
  | 'perle'
  | 'ardoise'
  | 'lagon'
  | 'menthe'
  | 'sauge'
  | 'miel'
  | 'abricot'
  | 'corail'
  | 'rose'
  | 'lavande'

export type TagPastelColors = {
  bg: string
  border: string
  fg: string
}

/** Dans l'ordre du sélecteur : le neutre, puis le tour du cercle chromatique. */
export const TAG_PASTELS: TagPastel[] = [
  'perle',
  'ardoise',
  'lagon',
  'menthe',
  'sauge',
  'miel',
  'abricot',
  'corail',
  'rose',
  'lavande',
]

export const tagPastelsLight: Record<TagPastel, TagPastelColors> = {
  perle: { bg: '#F1F3F6', border: '#DFE3E9', fg: '#556070' },
  ardoise: { bg: '#EAF0F5', border: '#D3DEE8', fg: '#34597A' },
  lagon: { bg: '#E6F4F8', border: '#C6E6EF', fg: '#1F6F86' },
  menthe: { bg: '#E6F7F6', border: '#B9E8E5', fg: '#0A7672' },
  sauge: { bg: '#EEF5E9', border: '#D7E8CB', fg: '#46732F' },
  miel: { bg: '#FBF5E0', border: '#F0E3B3', fg: '#7D6516' },
  abricot: { bg: '#FDF1E6', border: '#F6DCC2', fg: '#9A5A1C' },
  corail: { bg: '#FCEEEC', border: '#F5D3CE', fg: '#A9433A' },
  rose: { bg: '#FBEDF3', border: '#F3D3E1', fg: '#A13D6B' },
  lavande: { bg: '#F1EDFB', border: '#DDD3F4', fg: '#6447A8' },
}

const withAlpha = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Le DS ne donne pas de sombre : on le dérive du clair. Fond = le texte à 28 % sur la surface,
 * texte = la bordure claire. Au moins 8,8:1 sur les surfaces sombres du thème.
 */
export const tagPastelsDark = Object.fromEntries(
  TAG_PASTELS.map((pastel) => {
    const { border, fg } = tagPastelsLight[pastel]
    return [
      pastel,
      { bg: withAlpha(fg, 0.28), border: withAlpha(fg, 0.55), fg: border },
    ]
  })
) as Record<TagPastel, TagPastelColors>

export const getTagPastels = (
  mode: PaletteMode = 'light'
): Record<TagPastel, TagPastelColors> =>
  mode === 'dark' ? tagPastelsDark : tagPastelsLight
